import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  ageFromBucket,
  catalogVolumeToBodymap,
  coerceLocale,
  normalizeVolumeByMuscle,
  sessionEnergyReadout,
  volumeBySlugToHeatmap,
  type AgeBucket,
  type BodyFigure,
  type PlanStarter,
} from "@jackedlogs/shared";
import {
  BodyMap,
  Button,
  CaloriesEffortBlock,
  LoggerScreen,
  MaxBumpBanner,
  SessionSummaryView,
  WorkoutsView,
  resolveTheme,
  useBreakpoint,
  useExerciseNameBySlug,
  type EditingTarget,
} from "@jackedlogs/ui";

import { displayUnitOf, useProfileDoc } from "./useProfileDoc";
import { useThemePalette } from "./theme";
import { useSessionsList } from "./useSessionsList";
import { useSessionResult } from "./useSessionResult";
import { useSessionLoader } from "./useSessionLoader";
import { notifyViaWindow } from "./confirm";
import { useLoggerHostDeps } from "./logger/useLoggerHostDeps";
import { useMaxBumpFlowWeb } from "./maxes/useMaxBumpFlowWeb";
import type { ComposerRequest } from "./useCreateHubWeb";

// THE Workouts surface on web-rnw — the shared WorkoutsView list plus a
// session detail pane (the shared SessionSummaryView), AND (RW-18) the
// lifted Logger itself: this is the natural mount point per hostDeps.ts —
// the list is already here, and a fresh save is the list refreshing. Master-
// detail is a console concern: wide (>=768) shows the list beside the detail;
// narrow degrades to the phone flow (list, tap pushes to the detail with a
// back affordance). Delete-from-list stays unset (WorkoutsView's own
// documented mobile-only affordance — no destructive gesture on the web
// console). Repeat/Edit (2026-08-18): mobile's model is "tap the row opens
// edit directly"; the web console already had a distinct read-only detail
// pane (master-detail), so tap-to-select stays as is and Edit is a button
// inside the pane instead (SessionSummaryView's actionSlot) — same
// capability, a layout appropriate to the pane it lives in. Both route
// through useSessionLoader (web twin of the mobile hook), the SAME shared
// hydrate path (EditingTarget/mode on LoggerScreen) mobile's row-tap uses.
// Weights render in kg (web convention). The dual bodymap is the shared
// <BodyMap> (lifted 2026-07-01), painted from the session's volumeByMuscle
// with the theme's grey→peak ramp. The MAX-bump banner
// (lift/max-bump-banner-web, 2026-08-17) sits above the list, same as
// mobile's WorkoutsListScreen — useMaxBumpFlowWeb evaluates every save for
// a new/regressed MAX via the LoggerScreen's onSaved(kind, maxBumpInput).

const LIST_WIDTH = 360;

export function WorkoutsScreen({
  composerRequest,
  onComposerRequestHandled,
  onRequestSetBodyweight,
}: {
  /** RW-18: a `{kind:"workout"}` request from the Create hub auto-opens the
   *  Logger, mirroring PlanScreen's own composerRequest handling for
   *  program/plan requests. */
  composerRequest?: ComposerRequest | null;
  onComposerRequestHandled?: () => void;
  /** Deep-link to Settings → Body for the once-only bodyweight nudge
   *  (mirrors mobile's WorkoutsListScreen). Cross-tab, so the shell owns it. */
  onRequestSetBodyweight?: () => void;
} = {}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const { wide } = useBreakpoint();
  const list = useSessionsList();
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const detail = useSessionResult(selectedId);
  const { doc } = useProfileDoc();
  const [loggerOpen, setLoggerOpen] = useState(false);
  // The Plan tab's "Start" (today's session / a program-highlight card)
  // hands its built PlanStarter through the SAME composerRequest channel
  // "New workout" uses — undefined for a plain blank open. Held in its own
  // state (not read back off composerRequest, which the effect below clears
  // immediately) so it survives for the Logger's whole open lifetime.
  const [planStarter, setPlanStarter] = useState<PlanStarter | null>(null);
  const [editing, setEditing] = useState<EditingTarget | null>(null);
  const loggerDeps = useLoggerHostDeps();
  const maxBumpFlow = useMaxBumpFlowWeb();
  // Edit/Repeat's movement-pattern hydration reuses the SAME catalog
  // useMaxBumpFlowWeb already fetches (best-effort, on mount) for its own
  // exercise-name lookup — no second catalog fetch/cache for this screen.
  // ⛔ E lo usa anche il pannello di dettaglio, che prima passava
  // `(slug, fallback) => fallback ?? slug`: senza lo snapshot del nome — le
  // righe vecchie e quelle importate non ce l'hanno — l'atleta leggeva lo
  // SLUG. Il risolutore giusto era gia' su questa superficie, dieci righe
  // sotto (il mount web di PostSessionSummaryCard lo fa da sempre).
  const resolveName = useExerciseNameBySlug(maxBumpFlow.catalog);
  const { openingEditId, repeatingId, onOpenEdit, onRepeat } = useSessionLoader({
    ensureCatalog: async () => maxBumpFlow.catalog ?? [],
    setEditing,
    setLoggerOpen,
    setError: (code) => {
      if (code) notifyViaWindow(t("mobile.workouts.loadError"), t("errors.generic"));
    },
    t,
  });

  // Create-hub "New workout" / Plan-tab "Start" → open the Logger, same
  // pattern PlanScreen already uses for program/plan requests (state-based
  // nav, no router).
  useEffect(() => {
    if (composerRequest?.kind !== "workout") return;
    setPlanStarter(composerRequest.planStarter ?? null);
    setLoggerOpen(true);
    onComposerRequestHandled?.();
  }, [composerRequest, onComposerRequestHandled]);
  // The full Session (with measured calories/HR + estimate) — the SessionResult
  // detail lacks them; the list items ARE full Sessions, so pluck the selected
  // one (selection always comes from the loaded list). Web has no on-device
  // hcWins toggle → default ON (HC wins when present).
  const selectedSession = useMemo(
    () =>
      selectedId ? (list.items?.find((s) => s.id === selectedId) ?? null) : null,
    [selectedId, list.items],
  );
  // Web has no on-device hcWins toggle → default ON (HC wins when present). One
  // shared derivation of the readout, same as the mobile logger (HC-8 B2c).
  const energyReadout = selectedSession
    ? sessionEnergyReadout(selectedSession, {
        hcWins: true,
        ageYears: ageFromBucket(doc?.["ageBucket"] as AgeBucket | undefined),
      })
    : null;
  const figure = doc?.["preferredFigure"] as BodyFigure | undefined;

  // Muscle heatmap for the selected session — same derivation as the mobile
  // post-session card (volumeByMuscle → catalog vocab → 5-bucket ramp), so the
  // web session detail paints the identical figure.
  const bodyMapValue = useMemo(() => {
    if (detail.result === null) return null;
    return volumeBySlugToHeatmap(
      catalogVolumeToBodymap(normalizeVolumeByMuscle(detail.result.volumeByMuscle)),
      theme.heatmapRamp,
    );
  }, [detail.result, theme.heatmapRamp]);

  const toggleMonth = (key: string) =>
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const listPane = (
    <View style={styles.flex}>
      <View style={[styles.newWorkoutRow, { borderBottomColor: theme.palette.border }]}>
        <Button
          theme={theme}
          icon="plus"
          label={t("createHub.newWorkout.title", { defaultValue: "New workout" })}
          onPress={() => {
            setPlanStarter(null);
            setLoggerOpen(true);
          }}
          full
        />
      </View>
      <MaxBumpBanner
        pendingMaxBumps={maxBumpFlow.pendingMaxBumps}
        catalog={maxBumpFlow.catalog}
        maxBumpBusy={maxBumpFlow.maxBumpBusy}
        onConfirmMaxBump={maxBumpFlow.onConfirmMaxBump}
        onDismissMaxBump={maxBumpFlow.onDismissMaxBump}
        onSnoozeMaxBump={maxBumpFlow.onSnoozeMaxBump}
        theme={theme}
        t={t}
      />
      <WorkoutsView
        theme={theme}
        items={list.items}
        error={list.error}
        refreshing={list.refreshing}
        loadingMore={list.loadingMore}
        cursor={list.cursor}
        collapsedMonths={collapsedMonths}
        onToggleMonth={toggleMonth}
        onRefresh={list.onRefresh}
        onEndReached={list.onEndReached}
        onSelectSession={setSelectedId}
        selectable
        selectedId={selectedId}
        enableRefreshControl={false}
        onRepeat={(id) => void onRepeat(id)}
        repeatingId={repeatingId}
        openingId={openingEditId}
      />
    </View>
  );

  const detailBody = (() => {
    if (selectedId === null) {
      return (
        <View style={styles.center}>
          <Text style={[styles.prompt, { color: theme.palette.ink2 }]}>
            {t("mobile.workouts.selectPrompt")}
          </Text>
        </View>
      );
    }
    if (detail.result === null && detail.status === "loading") {
      return (
        <View style={styles.center}>
          <ActivityIndicator color={theme.palette.ink2} />
        </View>
      );
    }
    if (detail.result === null) {
      return (
        <View style={styles.center}>
          <Text style={[styles.prompt, { color: theme.palette.danger }]}>
            {t("mobile.workouts.loadError")}
          </Text>
        </View>
      );
    }
    return (
      <ScrollView contentContainerStyle={styles.detailScroll}>
        <SessionSummaryView
          theme={theme}
          result={detail.result}
          locale={locale}
          unit={displayUnitOf(doc)}
          resolveName={resolveName}
          actionSlot={
            selectedId !== null ? (
              <Button
                theme={theme}
                variant="secondary"
                icon="edit"
                label={t("workout.edit")}
                onPress={() => void onOpenEdit(selectedId)}
                loading={openingEditId === selectedId}
                // Button's 40pt box relies on Pressable's hitSlop to reach the
                // 44pt floor (packages/ui/src/primitives/Button.tsx:17-18) —
                // that's RN-native behaviour; react-native-web's Pressable
                // doesn't implement hitSlop at all, so on this surface the
                // box itself must be 44 (a11y audit, 2026-08-18).
                style={styles.editButton}
              />
            ) : undefined
          }
          bodyMapSlot={
            bodyMapValue && Object.keys(bodyMapValue).length > 0 ? (
              <BodyMap
                mode="display"
                view="front"
                value={bodyMapValue}
                theme={theme}
                isDark={dark}
                figure={figure}
                layout="dual"
              />
            ) : undefined
          }
          energySlot={
            energyReadout ? (
              <CaloriesEffortBlock theme={theme} {...energyReadout} />
            ) : undefined
          }
        />
      </ScrollView>
    );
  })();

  // The Logger itself (RW-18). Opened by the "+ New workout" row above the
  // list or by the Create hub's "New workout" (composerRequest, effect
  // above). `onSaved` refreshes the list AND evaluates the save for a new
  // MAX (same LG-35 rule mobile follows: evaluate on every outcome, not
  // just a server-confirmed one — web never actually answers 'queued', but
  // the shape stays symmetric with mobile's). The Modal stays open to show
  // the post-save summary; the summary's own "Done" CTA closes it (onClose,
  // see useSaveWorkout's postSaveSummary.onDone), same as mobile.
  const loggerNode = (
    <LoggerScreen
      visible={loggerOpen}
      editing={editing}
      onClose={() => {
        setLoggerOpen(false);
        setPlanStarter(null);
        setEditing(null);
      }}
      planStarter={planStarter}
      onSaved={(kind, maxBumpInput) => {
        maxBumpFlow.evaluateAfterSave(maxBumpInput);
        if (kind === "sent") list.onRefresh();
      }}
      onRequestSetBodyweight={onRequestSetBodyweight}
      deps={loggerDeps}
    />
  );

  // Narrow (phone width): list, then push to the detail with a Back row.
  if (!wide) {
    return (
      <View style={[styles.flex, { backgroundColor: theme.palette.bg }]}>
        {selectedId === null ? (
          listPane
        ) : (
          <View style={styles.flex}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("mobile.common.back")}
              onPress={() => setSelectedId(null)}
              style={({ pressed }) => [
                styles.backRow,
                { borderBottomColor: theme.palette.border },
                pressed && { opacity: 0.6 },
              ]}
            >
              <Text style={[styles.backText, { color: theme.palette.brand500 }]}>
                {`← ${t("mobile.common.back")}`}
              </Text>
            </Pressable>
            {detailBody}
          </View>
        )}
        {loggerNode}
      </View>
    );
  }

  // Wide (console): list beside the detail pane.
  return (
    <View style={[styles.row, { backgroundColor: theme.palette.bg }]}>
      <View
        style={[styles.listPane, { borderRightColor: theme.palette.border }]}
      >
        {listPane}
      </View>
      <View style={styles.detailPane}>{detailBody}</View>
      {loggerNode}
    </View>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  row: { flex: 1, flexDirection: "row" },
  newWorkoutRow: { padding: 12, borderBottomWidth: StyleSheet.hairlineWidth },
  listPane: { width: LIST_WIDTH, borderRightWidth: StyleSheet.hairlineWidth },
  detailPane: { flex: 1, minWidth: 320 },
  center: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24 },
  prompt: { fontSize: 14, textAlign: "center" },
  detailScroll: { padding: 16 },
  editButton: { minHeight: 44, alignSelf: "flex-start" },
  backRow: {
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backText: { fontSize: 14, fontWeight: "600" },
});
